/**
 * AUTODOC 평가 모듈 - 관리자 대시보드 메인 페이지
 * 
 * 기존 AUTODOC React Router에 추가:
 *   <Route path="/eval" element={<EvalAdminPage />} />
 * 
 * 또는 사이드바 메뉴에 "차량 평가" 탭 추가
 */
import React, { useState, useEffect, useCallback } from 'react';
import { StatusBadge, LevelBadge, Stars, SummaryCard, EmptyState } from './CommonUI';
import OrderForm from './OrderForm';
import OrderDetail from './OrderDetail';
import InspectorManage from './InspectorManage';
import ScheduleCalendar from './ScheduleCalendar';
import { API, authFetch } from '../utils/api';
import { ORDER_STATUS, formatMoney } from '../utils/constants';
import CustomModal from './CustomModal';
import SettlementPage from './EvalSettlementPage';
import { getDateRange } from '../utils/dateRange';


// mobile detection hook
function useIsMobile(bp) { var b = bp || 768; var [m, setM] = useState(window.innerWidth <= b); useEffect(function() { var h = function() { setM(window.innerWidth <= b); }; window.addEventListener('resize', h); return function() { window.removeEventListener('resize', h); }; }, [b]); return m; }

export default function EvalAdminPage({ user }) {
  var isMobile = useIsMobile();
  var isAdmin = !user || user.role === '관리자' || user.role === 'admin';
  var [modal, setModal] = useState(null);
  var closeModal = function() { setModal(null); };
  var [orders, setOrders] = useState([]);
  var [inspectors, setInspectors] = useState([]);
  var [filter, setFilter] = useState('all');
  var [search, setSearch] = useState('');
  var [selectedId, setSelectedId] = useState(null);
  var [showForm, setShowForm] = useState(false);
  var [editingOrder, setEditingOrder] = useState(null);
  var [actionMenu, setActionMenu] = useState(null);
  var [page, setPage] = useState('orders');
  var [toast, setToast] = useState(null);
  var [dateFilter, setDateFilter] = useState('all');
  var [customFrom, setCustomFrom] = useState('');
  var [customTo, setCustomTo] = useState('');
  var [showCustomDate, setShowCustomDate] = useState(false);
  // Wave W1 (2026-05-21) — 글로벌 mutation 로딩 상태 (등록/상태변경/매입/취소/삭제/리뷰)
  var [submitting, setSubmitting] = useState(false);

  // Wave W2 (2026-05-21) — 통합 에러 모달. 모든 mutation/fetch catch 의 silent 제거.
  //   네트워크/권한/서버 에러 즉시 사용자 인지 + 명확한 메시지.
  //   (선언을 fetchOrders/fetchInspectors 위로 hoist — catch 에서 선언 전 참조 방지.)
  var showError = function(err, defaultMsg) {
    var msg = (err && err.response && err.response.data && err.response.data.error) ||
              (err && err.message) ||
              (typeof err === 'string' ? err : null) ||
              defaultMsg || '오류가 발생했습니다';
    setModal({ type: 'danger', title: '실패', message: msg, hideCancel: true, onConfirm: closeModal });
  };

  // API 호출
  var fetchOrders = function() {
    authFetch(API.replace('/api', '') + '/api/eval/orders').then(function(r) { return r.json(); }).then(function(d) {
      if (d.success) setOrders(d.data || []);
    }).catch(function(e) { showError(e); });
  };
  var fetchInspectors = function() {
    authFetch(API.replace('/api', '') + '/api/eval/inspectors').then(function(r) { return r.json(); }).then(function(d) {
      if (d.success) setInspectors(d.data || []);
    }).catch(function(e) { showError(e); });
  };

  useEffect(function() { fetchOrders(); fetchInspectors(); }, []);

  // 브라우저 뒤로가기 연동
  var navTo = function(newPage, newSelectedId) {
    setPage(newPage || 'orders');
    setSelectedId(newSelectedId || null);
    window.history.pushState({ evalPage: newPage || 'orders', evalSelectedId: newSelectedId || null }, '');
  };

  useEffect(function() {
    // 초기 히스토리 상태
    window.history.replaceState({ evalPage: 'orders', evalSelectedId: null }, '');
    var handlePop = function(e) {
      var st = e.state;
      if (st && st.evalPage !== undefined) {
        setPage(st.evalPage || 'orders');
        setSelectedId(st.evalSelectedId || null);
      } else {
        // eval 외부로 나가는 뒤로가기 방지 — eval 목록으로 복귀
        if (page !== 'orders' || selectedId) {
          setPage('orders');
          setSelectedId(null);
          window.history.pushState({ evalPage: 'orders', evalSelectedId: null }, '');
        }
      }
    };
    window.addEventListener('popstate', handlePop);
    return function() { window.removeEventListener('popstate', handlePop); };
  }, []);

  var dateRange = getDateRange(dateFilter, customFrom, customTo);

  // 기간+상태+검색 필터링
  var dateFilteredOrders = dateRange ? orders.filter(function(o) {
    var d = o.inspect_date || (o.created_at ? o.created_at.substring(0, 10) : '');
    return d >= dateRange.from && d <= dateRange.to;
  }) : orders;

  var filteredOrders = dateFilteredOrders.filter(function(o) {
    if (filter !== 'all' && o.status !== filter) return false;
    if (search) {
      var s = search.toLowerCase();
      return (
        (o.car_model || '').toLowerCase().indexOf(s) >= 0 ||
        (o.car_number || '').toLowerCase().indexOf(s) >= 0 ||
        (o.requester || '').toLowerCase().indexOf(s) >= 0 ||
        (o.inspector_name || '').toLowerCase().indexOf(s) >= 0 ||
        (o.location || '').toLowerCase().indexOf(s) >= 0
      );
    }
    return true;
  });

  // 상태별 카운트 (기간 필터 반영)
  var counts = {};
  dateFilteredOrders.forEach(function(o) { counts[o.status] = (counts[o.status] || 0) + 1; });

  var BASE = API.replace('/api', '');

  // 토스트 표시
  var showToast = function(msg) { setToast(msg); setTimeout(function() { setToast(null); }, 3000); };

  // 신규 오더 등록
  var handleCreateOrder = function(formData) {
    setSubmitting(true);
    authFetch(BASE + '/api/eval/orders', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData),
    }).then(function(r) { return r.json(); }).then(function(d) {
      if (d.success) { fetchOrders(); setShowForm(false); showToast(formData.car_model + ' 콜 등록 완료'); }
    }).catch(function(e) { showError(e); }).finally(function() { setSubmitting(false); });
  };

  // 상태 변경
  var handleStatusChange = function(orderId, newStatus) {
    var endpoint = newStatus === 'rejected' ? 'reject' : newStatus;
    setSubmitting(true);
    authFetch(BASE + '/api/eval/orders/' + orderId + '/' + endpoint, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
    }).then(function() { fetchOrders(); showToast(ORDER_STATUS[newStatus]?.label || newStatus); })
      .catch(function(e) { showError(e); }).finally(function() { setSubmitting(false); });
  };

  // 매입 결정
  var handlePurchase = function(orderId, purchaseData) {
    setSubmitting(true);
    authFetch(BASE + '/api/eval/orders/' + orderId + '/decide', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(purchaseData),
    }).then(function(r) { return r.json(); }).then(function(d) {
      if (d.success) {
        fetchOrders(); navTo('orders');
        var ocrMsg = '';
        if (d.data.ocr_success && d.data.ocr_result) {
          var oc = d.data.ocr_result;
          ocrMsg = '\n✅ OCR: ' + (oc.carNo || '-') + ' / ' + (oc.owner || '-') + ' / ' + (oc.vin || '-');
        } else { ocrMsg = '\n⚠️ 등록증 OCR 미적용 (수동 확인 필요)'; }
        showToast((d.data.car_model || '') + ' AUTODEX 차량 등록 완료 (ID: ' + d.data.vehicle_id + ')' + ocrMsg);
      } else { setModal({ type: 'danger', title: '실패', message: d.error || '매입 처리 실패', hideCancel: true, onConfirm: closeModal }); }
    }).catch(function(e) { showError(e); }).finally(function() { setSubmitting(false); });
  };

  // 리뷰 제출
  var handleSubmitReview = function(orderId, inspectorId, review) {
    setSubmitting(true);
    authFetch(BASE + '/api/eval/inspectors/' + inspectorId + '/review', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        order_id: orderId,
        score_speed: review.speed, score_accuracy: review.accuracy,
        score_detail: review.detail, score_overall: review.overall,
        comment: review.comment,
      }),
    }).then(function() { fetchOrders(); showToast('리뷰 등록 완료'); })
      .catch(function(e) { showError(e); }).finally(function() { setSubmitting(false); });
  };

  // 오더 삭제
  var handleDeleteOrder = function(orderId) {
    setModal({
      type: 'danger',
      title: '이 항목을 삭제할까요?',
      message: '관련 검차 결과, 사진도 모두 삭제됩니다.',
      confirmText: '삭제',
      onConfirm: function() {
        closeModal();
        setSubmitting(true);
        authFetch(BASE + '/api/eval/orders/' + orderId, { method: 'DELETE' })
          .then(function(r) { return r.json(); }).then(function(d) {
            if (d.success) { fetchOrders(); showToast('오더 삭제 완료'); }
            else setModal({ type: 'danger', title: '실패', message: d.error || '삭제 실패', hideCancel: true, onConfirm: closeModal });
          }).catch(function(e) { showError(e); }).finally(function() { setSubmitting(false); });
      }
    });
  };

  // 오더 취소
  var handleCancelOrder = function(orderId) {
    setModal({
      type: 'confirm',
      title: '오더를 취소할까요?',
      confirmText: '취소',
      onConfirm: function() {
        closeModal();
        setSubmitting(true);
        authFetch(BASE + '/api/eval/orders/' + orderId + '/cancel', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
        }).then(function() { fetchOrders(); showToast('오더 취소됨'); })
          .catch(function(e) { showError(e); }).finally(function() { setSubmitting(false); });
      }
    });
  };

  // 오더 수정
  var handleUpdateOrder = function(orderId, formData) {
    authFetch(BASE + '/api/eval/orders/' + orderId, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData),
    }).then(function(r) { return r.json(); }).then(function(d) {
      if (d.success) { fetchOrders(); setEditingOrder(null); showToast('오더 수정 완료'); }
      else setModal({ type: 'danger', title: '실패', message: d.error || '수정 실패', hideCancel: true, onConfirm: closeModal });
    }).catch(function(e) { showError(e); });
  };

  // ── 평가사 관리 ──
  if (page === 'inspectors') {
    return <InspectorManage onBack={function() { navTo('orders'); }} />;
  }

  // ── 캘린더 ──
  if (page === 'calendar') {
    return (
      <ScheduleCalendar
        orders={orders}
        inspectors={inspectors}
        onSelectOrder={function(id) { navTo('orders', id); }}
        onBack={function() { navTo('orders'); }}
      />
    );
  }

  // ── 정산 ──
  if (page === 'settlement') {
    return <SettlementPage BASE={BASE} onBack={function() { navTo('orders'); }} />;
  }

  // ── 상세 페이지 ──
  if (selectedId) {
    const order = orders.find(o => o.id === selectedId);
    if (!order) { navTo('orders'); return null; }
    const inspector = inspectors.find(i => i.id === order.inspector_id);
    return (
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '16px 12px' }}>
        <OrderDetail
          order={order}
          evalResult={order.eval_result}
          inspectorReview={order.inspector_review}
          inspector={inspector}
          onBack={function() { navTo('orders'); }}
          onStatusChange={handleStatusChange}
          onSubmitReview={handleSubmitReview}
          onPurchase={handlePurchase}
        />
        {modal && <CustomModal isOpen={true} {...modal} onCancel={closeModal} />}
      </div>
    );
  }

  // ── 목록 페이지 ──
  return (
    <div style={{ maxWidth: 640, margin: '0 auto', padding: '16px 12px' }}>
      {/* Wave W1 (2026-05-21) — 글로벌 로딩 오버레이. mutation 중 클릭 차단 + 시각 피드백. */}
      {submitting && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(15,23,42,0.45)', zIndex: 9998,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'auto',
        }}>
          <div style={{
            background: '#fff', borderRadius: 14, padding: '20px 32px',
            display: 'flex', alignItems: 'center', gap: 12,
            boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
          }}>
            <div className="spinner" style={{
              width: 22, height: 22, borderRadius: '50%',
              border: '3px solid #e5e7eb', borderTopColor: '#1B3A5C',
              animation: 'spin 0.8s linear infinite',
            }}/>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#1B3A5C' }}>처리 중...</span>
          </div>
          <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
        </div>
      )}
      {/* 토스트 */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)',
          background: '#1D9E75', color: '#fff', padding: '10px 24px',
          borderRadius: 10, fontSize: 14, fontWeight: 600, zIndex: 999,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        }}>
          {toast}
        </div>
      )}

      {/* 헤더 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h1 style={{ margin: 0, fontSize: isMobile ? 18 : 22, fontWeight: 800, color: '#111', letterSpacing: '-0.5px' }}>
          차량 평가
        </h1>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {isAdmin && (<button onClick={function() { navTo('settlement'); }} style={{
            padding: isMobile ? '6px 10px' : '8px 16px', borderRadius: 8,
            border: '1px solid #534AB7', background: '#fff',
            color: '#534AB7', cursor: 'pointer', fontSize: isMobile ? 11 : 13, fontWeight: 700,
          }}>정산</button>)}
          <button onClick={function() { navTo('calendar'); }} style={{
            padding: isMobile ? '6px 10px' : '8px 16px', borderRadius: 8,
            border: '1px solid #1D9E75', background: '#fff',
            color: '#1D9E75', cursor: 'pointer', fontSize: isMobile ? 11 : 13, fontWeight: 700,
          }}>캘린더</button>
          {isAdmin && (<button onClick={function() { navTo('inspectors'); }} style={{
            padding: isMobile ? '6px 10px' : '8px 16px', borderRadius: 8,
            border: '1px solid #EF9F27', background: '#fff',
            color: '#BA7517', cursor: 'pointer', fontSize: isMobile ? 11 : 13, fontWeight: 700,
          }}>평가사 ★</button>)}
          {!isMobile && <button onClick={() => setShowForm(!showForm)} style={{
            padding: '8px 16px', borderRadius: 8, border: 'none',
            background: '#3B8BD4', color: '#fff', cursor: 'pointer',
            fontSize: 13, fontWeight: 700, letterSpacing: '-0.3px',
          }}>+ 신규 콜</button>}
        </div>
      </div>

      {/* 신규 폼 */}
      {showForm && (
        <OrderForm onSubmit={handleCreateOrder} onCancel={function() { setShowForm(false); }} />
      )}

      {/* 수정 폼 */}
      {editingOrder && (
        <div style={{ background: '#fff', border: '2px solid #EF9F27', borderRadius: 14, padding: 18, marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: '#222' }}>오더 수정 — {editingOrder.order_no}</span>
            <button onClick={function() { setEditingOrder(null); }} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#888' }}>×</button>
          </div>
          <OrderForm
            initialData={editingOrder}
            onSubmit={function(formData) { handleUpdateOrder(editingOrder.id, formData); }}
            onCancel={function() { setEditingOrder(null); }}
            submitLabel="수정 저장"
          />
        </div>
      )}

      {/* 상태 카운트 카드 */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(5, 1fr)', gap: 5, marginBottom: 14 }}>
        {[
          ['open', '대기', counts.open || 0],
          ['taken', '수락', counts.taken || 0],
          ['inspecting', '검차중', counts.inspecting || 0],
          ['completed', '완료', counts.completed || 0],
          ['decided', '매입', counts.decided || 0],
        ].map(function(item) { var key=item[0], label=item[1], count=item[2]; return (
          <SummaryCard
            key={key}
            label={label}
            value={count}
            color={ORDER_STATUS[key]?.color}
            bg={filter === key ? ORDER_STATUS[key]?.bg : '#f8f8f5'}
            onClick={function() { setFilter(function(f) { return f === key ? 'all' : key; }); }}
          />
        ); })}
      </div>

      {/* 기간 필터 */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {[['all', '전체'], ['today', '오늘'], ['week', '이번주'], ['month', '이번달']].map(function(item) {
          var k = item[0], l = item[1];
          var active = dateFilter === k;
          return (
            <button key={k} onClick={function() { setDateFilter(k); setShowCustomDate(false); }} style={{
              padding: '6px 14px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
              border: active ? 'none' : '1px solid #d0d0c8',
              background: active ? '#1B3A5C' : '#fff',
              color: active ? '#fff' : '#666',
              fontWeight: active ? 600 : 400,
            }}>{l}</button>
          );
        })}
        <button onClick={function() { setShowCustomDate(!showCustomDate); if (!showCustomDate) setDateFilter('custom'); }} style={{
          padding: '6px 10px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
          border: dateFilter === 'custom' ? 'none' : '1px solid #d0d0c8',
          background: dateFilter === 'custom' ? '#1B3A5C' : '#fff',
          color: dateFilter === 'custom' ? '#fff' : '#666',
        }}>📅</button>
        {showCustomDate && (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <input type="date" value={customFrom} onChange={function(e) { setCustomFrom(e.target.value); setDateFilter('custom'); }} style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #d0d0c8', fontSize: 12 }} />
            <span style={{ fontSize: 12, color: '#888' }}>~</span>
            <input type="date" value={customTo} onChange={function(e) { setCustomTo(e.target.value); setDateFilter('custom'); }} style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #d0d0c8', fontSize: 12 }} />
          </div>
        )}
      </div>

      {/* 검색 */}
      <input
        value={search}
        onChange={function(e) { setSearch(e.target.value); }}
        placeholder="차종, 번호, 요청자, 위치 검색..."
        style={{
          width: '100%', boxSizing: 'border-box', padding: '10px 14px',
          border: '1px solid #e0dfda', borderRadius: 10, fontSize: 14,
          outline: 'none', marginBottom: 12, background: '#fff',
        }}
      />

      {/* 필터 탭 */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 12, flexWrap: 'wrap' }}>
        {[
          { key: 'all', label: '전체' },
          ...Object.entries(ORDER_STATUS).map(([k, v]) => ({ key: k, label: v.label })),
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            style={{
              padding: '5px 12px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
              border: filter === f.key ? '1.5px solid #3B8BD4' : '1px solid #e0dfda',
              background: filter === f.key ? '#E6F1FB' : '#fff',
              color: filter === f.key ? '#185FA5' : '#888',
              fontWeight: filter === f.key ? 600 : 400,
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* 오더 리스트 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} onClick={function() { setActionMenu(null); }}>
        {filteredOrders.length === 0 && <EmptyState message="조건에 맞는 오더가 없습니다" />}
        {filteredOrders.map(order => {
          const ins = inspectors.find(i => i.id === order.inspector_id);
          return (
            <div
              key={order.id}
              onClick={function() { navTo('orders', order.id); }}
              style={{
                background: '#fff', borderRadius: 12,
                border: '1px solid #eeeee8', padding: '14px 16px',
                cursor: 'pointer', transition: 'border-color 0.15s, box-shadow 0.15s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = '#ccc';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = '#eeeee8';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* 첫 줄: 모델 + 상태 + 액션 */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <div>
                  <span style={{ fontSize: 15, fontWeight: 700, color: '#111' }}>{order.car_model}</span>
                  {order.car_number && (
                    <span style={{ fontSize: 12, color: '#aaa', marginLeft: 8, fontWeight: 400 }}>{order.car_number}</span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <StatusBadge status={order.status} />
                  <div style={{ position: 'relative' }}>
                    <button onClick={function(e) { e.stopPropagation(); setActionMenu(actionMenu === order.id ? null : order.id); }} style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', color: '#aaa', padding: '0 4px', lineHeight: 1 }}>⋮</button>
                    {actionMenu === order.id && (
                      <div style={{ position: 'absolute', right: 0, top: 24, background: '#fff', border: '1px solid #eee', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.1)', zIndex: 100, minWidth: 100, overflow: 'hidden' }}>
                        <div onClick={function(e) { e.stopPropagation(); setEditingOrder(order); setActionMenu(null); }} style={{ padding: '8px 14px', fontSize: 13, cursor: 'pointer', color: '#222' }} onMouseEnter={function(e) { e.target.style.background = '#f5f5f0'; }} onMouseLeave={function(e) { e.target.style.background = '#fff'; }}>수정</div>
                        {order.status !== 'decided' && <div onClick={function(e) { e.stopPropagation(); setActionMenu(null); handleCancelOrder(order.id); }} style={{ padding: '8px 14px', fontSize: 13, cursor: 'pointer', color: '#BA7517' }} onMouseEnter={function(e) { e.target.style.background = '#f5f5f0'; }} onMouseLeave={function(e) { e.target.style.background = '#fff'; }}>취소</div>}
                        {order.status !== 'decided' && <div onClick={function(e) { e.stopPropagation(); setActionMenu(null); handleDeleteOrder(order.id); }} style={{ padding: '8px 14px', fontSize: 13, cursor: 'pointer', color: '#E24B4A' }} onMouseEnter={function(e) { e.target.style.background = '#f5f5f0'; }} onMouseLeave={function(e) { e.target.style.background = '#fff'; }}>삭제</div>}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 둘째 줄: 정보 */}
              <div style={{ fontSize: 12, color: '#888', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <span>{order.car_year}년 · {(order.car_mileage || 0).toLocaleString()}km</span>
                <span>{order.location}</span>
                <span>요청: {order.requester}</span>
                {order.inspect_date && <span>{order.inspect_date} {order.inspect_time}</span>}
              </div>

              {/* 셋째 줄: 검차원 + 평가 */}
              {(ins || order.eval_result) && (
                <div style={{ fontSize: 12, color: '#888', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 4 }}>
                  {ins && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      검차: {ins.name} <LevelBadge level={ins.level} /> {ins.avg_rating}★
                    </span>
                  )}
                  {order.eval_result && (
                    <span>평가: <Stars value={order.eval_result.score_overall} size={11} /></span>
                  )}
                  {order.status === 'completed' && !order.inspector_review && order.inspector_id && (
                    <span style={{ color: '#EF9F27', fontWeight: 600 }}>리뷰 대기</span>
                  )}
                  {order.purchase_price && (
                    <span style={{ color: '#D85A30', fontWeight: 600 }}>매입 {formatMoney(order.purchase_price)}</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* 모바일 FAB */}
      {isMobile && !showForm && (
        <button onClick={function() { setShowForm(true); }} style={{
          position: 'fixed', bottom: 80, right: 24, width: 56, height: 56,
          borderRadius: '50%', border: 'none', background: '#3B8BD4', color: '#fff',
          fontSize: 28, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 20px rgba(59,139,212,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, lineHeight: 1,
        }}>+</button>
      )}

      {/* 모바일 전체화면 폼 */}
      {isMobile && showForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: '#fff', zIndex: 200, overflow: 'auto', padding: '12px 16px' }}>
          <OrderForm onSubmit={handleCreateOrder} onCancel={function() { setShowForm(false); }} />
        </div>
      )}

      {modal && <CustomModal isOpen={true} {...modal} onCancel={closeModal} />}
    </div>
  );
}
